import { useEffect, useRef, useState } from "react";
import { ensurePdfWorker } from "../utils/pdfWorker";
import { getDocument } from "pdfjs-dist";
import { useLanguage } from "../i18n/LanguageContext";

function PdfPage({ pdf, number }) {
  const host = useRef(null);
  const [width, setWidth] = useState(0);
  const [visible, setVisible] = useState(false);
  const [error, setError] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const element = host.current;
    const resize = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    resize.observe(element);
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { rootMargin: "600px" });
    observer.observe(element);
    return () => { resize.disconnect(); observer.disconnect(); };
  }, []);

  useEffect(() => {
    if (!visible || !width) return undefined;
    let cancelled = false;
    let task;
    const element = host.current;
    const canvas = document.createElement("canvas");
    setError(false);
    pdf.getPage(number).then(async (page) => {
      if (cancelled) return;
      const base = page.getViewport({ scale: 1 });
      element.style.aspectRatio = `${base.width} / ${base.height}`;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const viewport = page.getViewport({ scale: width / base.width * ratio });
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      canvas.setAttribute("role", "img");
      canvas.setAttribute("aria-label", `${t("Page")} ${number}`);
      element.replaceChildren(canvas);
      task = page.render({ canvasContext: canvas.getContext("2d"), viewport });
      await task.promise;
    }).catch((err) => { if (!cancelled && err.name !== "RenderingCancelledException") setError(true); });
    return () => { cancelled = true; task?.cancel(); canvas.remove(); canvas.width = 0; canvas.height = 0; };
  }, [pdf, number, width, visible, t]);

  return <section className="hpa-pdf-page">
    <div ref={host} className="hpa-pdf-page__canvas" />
    {error && <p role="alert">{t("The guide could not be opened. Please try again.")}</p>}
    <p className="hpa-pdf-page__number">{t("Page")} {number} / {pdf.numPages}</p>
  </section>;
}

export default function ResponsivePdf({ url }) {
  const [pdf, setPdf] = useState(null);
  const [error, setError] = useState(false);
  const { t } = useLanguage();
  useEffect(() => {
    let active = true;
    let task;
    setPdf(null);
    setError(false);

    ensurePdfWorker().then(() => {
      if (!active) return;
      task = getDocument({ url: url.split("#")[0], isEvalSupported: false });
      task.promise.then((document) => { if (active) setPdf(document); })
        .catch(() => { if (active) setError(true); });
    });

    return () => { active = false; task?.destroy(); };
  }, [url]);
  if (error) return <p role="alert">{t("The guide could not be opened. Please try again.")}</p>;
  if (!pdf) return <div className="hpa-feature-state" role="status">{t("Loading...")}</div>;
  return <div className="hpa-pdf-pages">{Array.from({ length: pdf.numPages }, (_, index) =>
    <PdfPage key={index + 1} pdf={pdf} number={index + 1} />)}</div>;
}
